import React from "react";

export const formatUtcDateTime = (iso?: string | null): string => {
  if (!iso) return "-";
  return `${new Date(iso).toLocaleString("en-GB", {
    timeZone: "UTC",
    hour12: false,
  })} UTC`;
};

// Display only: the admin's own time for a stored UTC instant, with the offset in force at that
// moment (DST included), so the value can never be read ambiguously.
export const formatLocalDateTime = (iso?: string | null): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const offset = -date.getTimezoneOffset();
  const abs = Math.abs(offset);
  const utcOffset = `UTC${offset < 0 ? "-" : "+"}${String(
    Math.floor(abs / 60),
  ).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  const zoneName = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
  const zone =
    zoneName && !/^(GMT|UTC)/.test(zoneName)
      ? `${zoneName} (${utcOffset})`
      : utcOffset;
  return `${date.toLocaleString("en-GB", { hour12: false })} ${zone}`;
};

export const renderScheduleCell = (field: "startDate" | "endDate") => (row: any) => {
  if (!row?.[field]) return "-";
  return React.createElement(
    "div",
    null,
    row[`${field}Text`],
    React.createElement(
      "div",
      { style: { fontSize: 11, color: "#6b6b6b", marginTop: 2 } },
      formatLocalDateTime(row[field]),
    ),
  );
};

// Same rule as PackEditor: once dates are set the schedule owns the status, and an active item with
// no dates is managed by hand, so its dates stay locked until the status is switched off.
export const getScheduleLockState = ({
  isLive,
  startDate,
  endDate,
}: {
  isLive: boolean;
  startDate?: string | null;
  endDate?: string | null;
}) => {
  const hasSchedule = !!startDate || !!endDate;
  return {
    hasSchedule,
    isStatusScheduleControlled: hasSchedule,
    isScheduleBlockedByStatus: isLive && !hasSchedule,
  };
};

type ContentSchedule = {
  startDate?: string | null;
  endDate?: string | null;
};

// Same checks as the server. A saved date may already be in the past, so only a changed one is checked.
export const assertScheduleForSubmit = (
  { startDate, endDate }: ContentSchedule,
  savedSchedule: ContentSchedule,
) => {
  const now = Date.now();
  if (
    startDate &&
    startDate !== savedSchedule.startDate &&
    new Date(startDate).getTime() <= now
  ) {
    throw new Error("Start date must be in the future.");
  }
  if (
    endDate &&
    endDate !== savedSchedule.endDate &&
    new Date(endDate).getTime() <= now
  ) {
    throw new Error("End date must be in the future.");
  }
  if (
    startDate &&
    endDate &&
    new Date(endDate).getTime() <= new Date(startDate).getTime()
  ) {
    throw new Error("End date must be after the start date.");
  }
};
